// Popover menus, ported from the glasshouse menuHost: the same two answers
// everywhere, Later with a real date and No thanks with a stated scope.
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { fireDateFor, todayInTz } from "../../shared/dates.js";
import { DECLINE_REASONS, SNOOZE_OPTIONS, SNOOZE_REASONS } from "../copy.js";
import { useStore } from "../state/store.js";

function prettyDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

function MenuFrame({ x, y, children }: { x?: number; y?: number; children: React.ReactNode }) {
  const closePopover = useStore((s) => s.closePopover);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    // Light dismiss: any click outside the menu closes it and is
    // swallowed (capture phase), so it cannot also close the sheet
    // underneath or re-trigger the button that opened the menu.
    // Registered a tick late, because the click that opened the menu is
    // still in flight when this mounts.
    const onClick = (ev: MouseEvent) => {
      if (ref.current && ev.target instanceof Node && ref.current.contains(ev.target)) return;
      ev.stopPropagation();
      ev.preventDefault();
      closePopover();
    };
    const t = window.setTimeout(() => document.addEventListener("click", onClick, true), 0);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener("click", onClick, true);
    };
  }, [closePopover]);

  // Anchored menus are measured after render and clamped to the viewport,
  // so a tall menu opened near the bottom of the screen slides up into
  // view instead of getting cut off. Re-measured when the content swaps
  // (duration list to reasons), which changes the height.
  useLayoutEffect(() => {
    if (x === undefined || y === undefined) return;
    const el = ref.current;
    if (!el) return;
    const h = el.offsetHeight;
    const w = el.offsetWidth || 294;
    const top = Math.max(12, Math.min(window.innerHeight - h - 12, y + 6));
    const left = Math.max(12, Math.min(window.innerWidth - w - 12, x - w));
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, [x, y, children]);

  const style =
    x !== undefined && y !== undefined
      ? (pos ?? { top: y + 6, left: Math.max(12, x - 294), visibility: "hidden" as const })
      : { top: "50%", left: "50%", transform: "translate(-50%,-50%)" };
  return (
    <div ref={ref} className="menu" style={style} onClick={(e) => e.stopPropagation()}>
      {children}
    </div>
  );
}

function SnoozeMenu({ cardId, x, y, duration, planItemId }: {
  cardId: string;
  x: number;
  y: number;
  duration?: string;
  planItemId?: string;
}) {
  const user = useStore((s) => s.user);
  const snoozeCard = useStore((s) => s.snoozeCard);
  const openPopover = useStore((s) => s.openPopover);
  const closePopover = useStore((s) => s.closePopover);
  const today = todayInTz(user?.timezone ?? "UTC");

  if (!duration) {
    return (
      <MenuFrame x={x} y={y}>
        <div className="mh">Come back to this</div>
        {SNOOZE_OPTIONS.map((o) => (
          <button
            key={o.id}
            className="mi"
            onClick={() => openPopover({ kind: "snooze", cardId, x, y, duration: o.id, planItemId })}
          >
            <b>{o.label}</b>
            <em>{prettyDate(fireDateFor(o.id, today))}</em>
          </button>
        ))}
        <div className="mf">
          {planItemId
            ? "Later takes it out of your practice and brings it back exactly once, on its date."
            : "Later is a real answer. It comes back exactly once, on its date."}
        </div>
      </MenuFrame>
    );
  }

  return (
    <MenuFrame x={x} y={y}>
      <div className="mh">Why, if you don’t mind, optional</div>
      <div className="chips">
        {SNOOZE_REASONS.map((r) => (
          <button
            key={r}
            className="chip"
            onClick={() => {
              void snoozeCard(cardId, duration, r, planItemId);
              closePopover();
            }}
          >
            {r}
          </button>
        ))}
      </div>
      <button
        className="mi"
        onClick={() => {
          void snoozeCard(cardId, duration, null, planItemId);
          closePopover();
        }}
      >
        <b>Skip, just remind me</b>
      </button>
      <div className="mf">
        This shapes what reaches you next. “My practice is already full” rests everything for a while; “costs too much” stops leading with the ones that need buying.
      </div>
    </MenuFrame>
  );
}

// Later for a practice you created yourself: a break, not a delete. Same
// real durations, no reasons step; it returns exactly once, on its date.
function PauseMenu({ planItemId, x, y }: { planItemId: string; x: number; y: number }) {
  const user = useStore((s) => s.user);
  const pausePlan = useStore((s) => s.pausePlan);
  const closePopover = useStore((s) => s.closePopover);
  const today = todayInTz(user?.timezone ?? "UTC");
  return (
    <MenuFrame x={x} y={y}>
      <div className="mh">Take a break until</div>
      {SNOOZE_OPTIONS.map((o) => (
        <button
          key={o.id}
          className="mi"
          onClick={() => {
            void pausePlan(planItemId, o.id);
            closePopover();
          }}
        >
          <b>{o.label}</b>
          <em>{prettyDate(fireDateFor(o.id, today))}</em>
        </button>
      ))}
      <div className="mf">
        A break, not a delete. It rests off Today and comes back exactly once, on its date. Start it again sooner from My Practice, under Deferred.
      </div>
    </MenuFrame>
  );
}

function DeclineMenu({ cardId, x, y, planItemId }: {
  cardId: string;
  x: number;
  y: number;
  planItemId?: string;
}) {
  const appState = useStore((s) => s.appState);
  const declineCard = useStore((s) => s.declineCard);
  const closePopover = useStore((s) => s.closePopover);
  const card = appState?.cards[cardId];
  const expert = card?.expertId ? appState?.experts[card.expertId] : undefined;
  const topic = appState?.topics.find((t) => t.id === card?.topicId);

  return (
    <MenuFrame x={x} y={y}>
      <div className="mh">{planItemId ? "Stop, and how far that goes" : "No thanks, and how far that goes"}</div>
      {DECLINE_REASONS.map((r) => {
        let label = r.label;
        if (r.key === "expert") label = `No practices from ${expert?.short ?? expert?.name ?? "them"}`;
        if (r.key === "topic") label = `No more ${topic?.label ?? "this topic"}`;
        const reason =
          r.key === "expert"
            ? `Nothing from ${expert?.short ?? expert?.name ?? "them"}`
            : r.key === "topic"
              ? `${topic?.short ?? "Topic"} declined`
              : r.label;
        return (
          <button
            key={r.key}
            className="mi"
            style={{ display: "block" }}
            onClick={() => {
              void declineCard(cardId, r.scope, reason, planItemId);
              closePopover();
            }}
          >
            <b>{label}</b>
            <span className="eff">{r.effect}</span>
          </button>
        );
      })}
      <div className="mf">
        Any of these can be undone from Discover, under Passed. None of it is held against you.
      </div>
    </MenuFrame>
  );
}

function NoteDialog({ title, body }: { title: string; body: string }) {
  const closePopover = useStore((s) => s.closePopover);
  return (
    <MenuFrame>
      <div className="mh">{title}</div>
      <div className="mf" style={{ border: 0, margin: 0 }}>
        {body}
      </div>
      <button className="mi" onClick={closePopover}>
        <b>Close</b>
      </button>
    </MenuFrame>
  );
}

export function PopoverHost() {
  const popover = useStore((s) => s.popover);
  if (!popover) return null;
  if (popover.kind === "snooze") return <SnoozeMenu {...popover} />;
  if (popover.kind === "decline") return <DeclineMenu {...popover} />;
  if (popover.kind === "pause") return <PauseMenu {...popover} />;
  return <NoteDialog title={popover.title} body={popover.body} />;
}

